import { useEffect, useState, type ReactNode } from 'react'
import { Sidebar } from './Sidebar'
import { Header } from './Header'
import { ImpersonationBanner } from '@/components/auth/ImpersonationBanner'
import { HelpFeedbackHost } from '@/components/helpFeedback/HelpFeedbackHost'
import { TourHost } from '@/components/tour/TourHost'
import { TriageRunToast } from '@/components/inbox/TriageRunToast'
import { AppListenersSlot } from '@/components/custom/CustomSlots'
import { cn } from '@/utils'
import { useNavBadges } from '@/hooks/useNavBadges'
import { useRecordUpdateNotices } from '@/hooks/useRecordUpdateNotices'
import { useCanAccessFeature } from '@/hooks/useCanAccessFeature'
import { startInboxEventStream } from '@/services/inboxEventBus'
import { useAppStore } from '@/stores'
import { useIsDesktop } from '@/hooks/useBreakpoint'

interface AppShellProps {
  children: ReactNode
}

export function AppShell({ children }: AppShellProps) {
  // appStore.isSidebarCollapsed is already synced under `preferences` and
  // reset on an identity change (SA-958 wires it; it was never read).
  const sidebarCollapsed = useAppStore((s) => s.isSidebarCollapsed)
  const setSidebarCollapsed = useAppStore((s) => s.setSidebarCollapsed)
  const isDesktop = useIsDesktop()
  // The collapse is a desktop rail: the phone drawer is always w-64 with
  // labels and has no Collapse control, so a collapse synced from a laptop
  // must never reach it. The stored value is left alone.
  const collapsed = sidebarCollapsed && isDesktop
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  useNavBadges()
  useRecordUpdateNotices()

  const canInbox = useCanAccessFeature('inbox', 'inbox:read')
  useEffect(() => {
    if (!canInbox) return
    return startInboxEventStream()
  }, [canInbox])

  return (
    // SA-1310: the one Help & feedback panel, opened from the user menus and failures.
    <HelpFeedbackHost>
      <div className="flex h-screen bg-background print:h-auto">
        {/* SA-1143: modules' app listeners (a notice toast); they render nothing on the page. */}
        <AppListenersSlot />
        {/* SA-1329: the toast when a bulk triage run ends. */}
        <TriageRunToast />
        {/* SA-1315: the page tour in progress; nothing while idle. */}
        <TourHost />
        {/* Mobile sidebar overlay */}
        {mobileSidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50 lg:hidden print:hidden"
            onClick={() => setMobileSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <Sidebar
          collapsed={collapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          mobileOpen={mobileSidebarOpen}
          onMobileClose={() => setMobileSidebarOpen(false)}
        />

        {/* Main content area */}
        <div className="flex flex-col flex-1 min-w-0">
          <ImpersonationBanner />
          {/* The phone's bar only (SA-965, D5): on desktop each page's own
              title band sits at the top, under the View-as banner. */}
          {!isDesktop && <Header onMenuClick={() => setMobileSidebarOpen(true)} />}

          {/* Page content. The scroll box is positioned so every absolute
              descendant (each sr-only span) takes it as its containing block;
              unpositioned, such a span sits at its static position below the
              window, stretches the document, and the wheel scrolls the whole
              shell off screen (SA-1189). */}
          <main
            className={cn(
              'relative flex-1 overflow-auto p-4 lg:p-6',
              'transition-all duration-300',
              'print:overflow-visible print:p-0'
            )}
          >
            {children}
          </main>
        </div>
      </div>
    </HelpFeedbackHost>
  )
}
